import axios from '@axios'
import Vue from 'vue'
import Vuex from 'vuex'
import i18n from '@/libs/i18n'

Vue.use(Vuex)
// lookupsStore
const lookupsStore = new Vuex.Store({
  namespaced: true,
  strict: process.env.NODE_ENV !== 'production',
  state: {
    lookups: null,
  },
  getters: {
    hasValues: state => key => (state.lookups !== null ? state.lookups.has(key) : false),
    getLookups: state => key => ((state.lookups !== null && state.lookups.has(key)) ? state.lookups.get(key) : null),
  },
  actions: {
    async load({ commit }, { table, aid, key }) {
      const values = (await axios.get(`/api/lookups/${table}${aid ? `?aid=${aid}` : ''}`)).data
      commit('setLookup', { key, values })
    },
    add({ commit }, { key, el }) { commit('addLookup', { key, el }) },
    reset({ commit }, { key }) { commit('resetLookup', { key }) },
    clear({ commit }) { commit('clear') },
  },
  mutations: {
    setLookup(state, { key, values }) {
      if (state.lookups === null) state.lookups = new Map()
      state.lookups.set(key, values)
    },
    addLookup(state, { key, el }) {
      if (state.lookups && state.lookups.has(key)) {
        const values = state.lookups.get(key)
        values.push({ label: el })
        state.lookups.set(key, values)
      }
    },
    resetLookup(state, { key }) {
      if (state.lookups && state.lookups.has(key)) {
        state.lookups.delete(key)
      }
    },
    clear(state) {
      state.lookups = null
    },
  },
})
// lookupsService
const lookupsService = {
  async getLookups(table, aid) {
    const key = `${table}${aid ? `.${aid}` : ''}`
    if (!lookupsStore.getters.hasValues(key)) { await lookupsStore.dispatch('load', { table, aid, key }) }
    const lookups = lookupsStore.getters.getLookups(key)
    if (lookups && Array.isArray(lookups) && lookups.length > 0) return lookups
    return [{ label: ' ' }]
  },
  async getLookupsNoCache(table, aid) {
    const items = await this.getLookups(table, aid)
    this.resetLookups(table, aid)
    return items
  },
  resetLookups(table, aid) {
    const key = `${table}${aid ? `.${aid}` : ''}`
    lookupsStore.dispatch('reset', { key })
  },
  clear: () => lookupsStore.dispatch('clear'),
  getUsers: () => lookupsService.getLookupsNoCache('users'),
  getAreas: () => lookupsService.getLookups('areas'),
  refreshAreas: () => lookupsService.resetLookups('areas'),
  getMaps: async (group, aid, obsoletes) => {
    let maps = await lookupsService.getLookups(`maps.${group}`, aid)
    const obsoGroup = (group.endsWith('.tags')) ? 'otags' : group
    if (obsoletes && obsoletes[obsoGroup] && obsoletes[obsoGroup].length > 0) {
      maps = maps.filter(x => !obsoletes[obsoGroup].includes(x.label))
    }
    if (!maps) return []
    return maps
  },
  replaceValues: async (group, aid, oldValue, newValue) => {
    if (oldValue && newValue && oldValue.trim() !== '' && newValue.trim() !== '') {
      await axios.post(`/api/lookups/replace.${group}`, JSON.stringify({ oldValue, newValue }), { params: { aid } })
      lookupsService.resetLookups(`maps.${group}`, aid)
    }
  },
  refreshRemoteMaps: async (group, aid, reload) => {
    await axios.post(`/api/lookups/refresh.${group}`, null, { params: { aid } })
    if (reload) lookupsService.resetLookups(`maps.${group}`, aid)
  },
  refreshMaps: (values, group, aid, actual) => {
    const key = `maps.${group}.${aid}`
    if (values) {
      let somethingChanged
      let existing = []
      if (lookupsStore.getters.hasValues(key)) existing = lookupsStore.getters.getLookups(key)
      const isArray = Array.isArray(values)
      const news = (isArray ? values : [values])
      for (let i = 0; i < news.length; i += 1) {
        const el = news[i]
        if (!existing.find(p => p.label === el)) {
          lookupsStore.dispatch('add', { key, el })
          if (actual) {
            if (actual.find(x => x.label === ' ')) actual.splice(0)
            actual.push(el)
          }
          somethingChanged = true
        }
      }
      if (somethingChanged) lookupsService.refreshRemoteMaps(group, aid)
    }
  },
  async refreshCached(areas) {
    if (!areas || areas.length === 0) return
    const promises = []
    areas.forEach(aid => {
      promises.push(...[
        lookupsService.getMaps('nations', aid),
        lookupsService.getMaps('donations.payments', aid),
        lookupsService.getMaps('donations.type', aid),
        lookupsService.getMaps('donations.category', aid),
        lookupsService.getMaps('donations.giversType', aid),
        lookupsService.getMaps('donations.tags', aid),
        lookupsService.getMaps('contributors.status', aid),
        lookupsService.getMaps('contributors.tags', aid),
        lookupsService.getMaps('contributors.contributorType', aid),
        lookupsService.getMaps('projects.tags', aid),
        lookupsService.getMaps('projects.missionName', aid),
        lookupsService.getMaps('projects.triboo', aid),
        lookupsService.getMaps('projects.status', aid),
        lookupsService.getMaps('projects.projectType', aid)])
    })
    await Promise.all(promises)
  },

  // hard-coded
  getGenderOptions: () => [
    { text: i18n.t('common.gender-M'), value: 'M' },
    { text: i18n.t('common.gender-F'), value: 'F' },
  ],
  getStateOptions: () => ['IT', 'CH'],
  getCurrencyOptions: () => ['EUR', 'CHF', 'USD'],
  getTaskStateOptions: () => [
    { text: i18n.t('common.status-todo'), value: 'todo' },
    { text: i18n.t('common.status-waiting'), value: 'waiting' },
    { text: i18n.t('common.status-completed'), value: 'completed' },
    { text: i18n.t('common.status-disabled'), value: 'disabled' },
  ],
  getZoneOptions: () => ['Asia', 'Africa', 'Europa', 'Nord America', 'Sud America', 'Australia/Oceania'],
  getRolesOptions: () => [
    { text: i18n.t('settings.role-write'), value: 'write' },
    { text: i18n.t('settings.role-delete'), value: 'delete' },
    { text: i18n.t('settings.role-limit-projects'), value: 'limit-projects' },
    { text: i18n.t('settings.role-admin'), value: 'admin' },

  ],
  getisChildrenOptions: () => [
    { text: i18n.t('projects.children-options-null'), value: null },
    { text: i18n.t('projects.children-options-true'), value: 1 },
    { text: i18n.t('projects.children-options-false'), value: 0 },
  ],
  getKantonOptions: () => [
    { value: 'AG', label: 'Aargau' },
    { value: 'AI', label: 'Appenzell Innerrhoden' },
    { value: 'AR', label: 'Appenzell Ausserrhoden' },
    { value: 'BE', label: 'Bern' },
    { value: 'BL', label: 'Basel-Landschaft' },
    { value: 'BS', label: 'Basel-Stadt' },
    { value: 'FR', label: 'Fribourg' },
    { value: 'GE', label: 'Genève' },
    { value: 'GL', label: 'Glarus' },
    { value: 'GR', label: 'Graubünden' },
    { value: 'JU', label: 'Jura' },
    { value: 'LU', label: 'Luzern' },
    { value: 'NE', label: 'Neuchâtel' },
    { value: 'NW', label: 'Nidwalden' },
    { value: 'OW', label: 'Obwalden' },
    { value: 'SG', label: 'St. Gallen' },
    { value: 'SH', label: 'Schaffausen' },
    { value: 'SO', label: 'Solothurn' },
    { value: 'SZ', label: 'Schwyz' },
    { value: 'TG', label: 'Turgau' },
    { value: 'TI', label: 'Ticino' },
    { value: 'UR', label: 'Uri' },
    { value: 'VD', label: 'Vaud' },
    { value: 'VS', label: 'Valais' },
    { value: 'ZG', label: 'Zug' },
    { value: 'ZH', label: 'Zürich' },
  ],
  getProvinceOptions: () => [
    { label: 'Agrigento', value: 'AG' },
    { label: 'Alessandria', value: 'AL' },
    { label: 'Ancona', value: 'AN' },
    { label: 'Aosta', value: 'AO' },
    { label: 'Arezzo', value: 'AR' },
    { label: 'Ascoli Piceno', value: 'AP' },
    { label: 'Asti', value: 'AT' },
    { label: 'Avellino', value: 'AV' },
    { label: 'Bari', value: 'BA' },
    { label: 'Barletta-Andria-Trani', value: 'BT' },
    { label: 'Belluno', value: 'BL' },
    { label: 'Benevento', value: 'BN' },
    { label: 'Bergamo', value: 'BG' },
    { label: 'Biella', value: 'BI' },
    { label: 'Bologna', value: 'BO' },
    { label: 'Bolzano', value: 'BZ' },
    { label: 'Brescia', value: 'BS' },
    { label: 'Brindisi', value: 'BR' },
    { label: 'Cagliari', value: 'CA' },
    { label: 'Caltanissetta', value: 'CL' },
    { label: 'Campobasso', value: 'CB' },
    { label: 'Carbonia-Iglesias', value: 'CI' },
    { label: 'Caserta', value: 'CE' },
    { label: 'Catania', value: 'CT' },
    { label: 'Catanzaro', value: 'CZ' },
    { label: 'Chieti', value: 'CH' },
    { label: 'Como', value: 'CO' },
    { label: 'Cosenza', value: 'CS' },
    { label: 'Cremona', value: 'CR' },
    { label: 'Crotone', value: 'KR' },
    { label: 'Cuneo', value: 'CN' },
    { label: 'Enna', value: 'EN' },
    { label: 'Fermo', value: 'FM' },
    { label: 'Ferrara', value: 'FE' },
    { label: 'Firenze', value: 'FI' },
    { label: 'Foggia', value: 'FG' },
    { label: 'Forlì-Cesena', value: 'FC' },
    { label: 'Frosinone', value: 'FR' },
    { label: 'Genova', value: 'GE' },
    { label: 'Gorizia', value: 'GO' },
    { label: 'Grosseto', value: 'GR' },
    { label: 'Imperia', value: 'IM' },
    { label: 'Isernia', value: 'IS' },
    { label: 'La Spezia', value: 'SP' },
    { label: 'L\'Aquila', value: 'AQ' },
    { label: 'Latina', value: 'LT' },
    { label: 'Lecce', value: 'LE' },
    { label: 'Lecco', value: 'LC' },
    { label: 'Livorno', value: 'LI' },
    { label: 'Lodi', value: 'LO' },
    { label: 'Lucca', value: 'LU' },
    { label: 'Macerata', value: 'MC' },
    { label: 'Mantova', value: 'MN' },
    { label: 'Massa-Carrara', value: 'MS' },
    { label: 'Matera', value: 'MT' },
    { label: 'Messina', value: 'ME' },
    { label: 'Milano', value: 'MI' },
    { label: 'Modena', value: 'MO' },
    { label: 'Monza e della Brianza', value: 'MB' },
    { label: 'Napoli', value: 'NA' },
    { label: 'Novara', value: 'NO' },
    { label: 'Nuoro', value: 'NU' },
    { label: 'Olbia-Tempio', value: 'OT' },
    { label: 'Oristano', value: 'OR' },
    { label: 'Padova', value: 'PD' },
    { label: 'Palermo', value: 'PA' },
    { label: 'Parma', value: 'PR' },
    { label: 'Pavia', value: 'PV' },
    { label: 'Perugia', value: 'PG' },
    { label: 'Pesaro e Urbino', value: 'PU' },
    { label: 'Pescara', value: 'PE' },
    { label: 'Piacenza', value: 'PC' },
    { label: 'Pisa', value: 'PI' },
    { label: 'Pistoia', value: 'PT' },
    { label: 'Pordenone', value: 'PN' },
    { label: 'Potenza', value: 'PZ' },
    { label: 'Prato', value: 'PO' },
    { label: 'Ragusa', value: 'RG' },
    { label: 'Ravenna', value: 'RA' },
    { label: 'Reggio Calabria', value: 'RC' },
    { label: 'Reggio Emilia', value: 'RE' },
    { label: 'Rieti', value: 'RI' },
    { label: 'Rimini', value: 'RN' },
    { label: 'Roma', value: 'RM' },
    { label: 'Rovigo', value: 'RO' },
    { label: 'Salerno', value: 'SA' },
    { label: 'Medio Campidano', value: 'VS' },
    { label: 'Sassari', value: 'SS' },
    { label: 'Savona', value: 'SV' },
    { label: 'Siena', value: 'SI' },
    { label: 'Siracusa', value: 'SR' },
    { label: 'Sondrio', value: 'SO' },
    { label: 'Taranto', value: 'TA' },
    { label: 'Teramo', value: 'TE' },
    { label: 'Terni', value: 'TR' },
    { label: 'Torino', value: 'TO' },
    { label: 'Ogliastra', value: 'OG' },
    { label: 'Trapani', value: 'TP' },
    { label: 'Trento', value: 'TN' },
    { label: 'Treviso', value: 'TV' },
    { label: 'Trieste', value: 'TS' },
    { label: 'Udine', value: 'UD' },
    { label: 'Varese', value: 'VA' },
    { label: 'Venezia', value: 'VE' },
    { label: 'Verbano-Cusio-Ossola', value: 'VB' },
    { label: 'Vercelli', value: 'VC' },
    { label: 'Verona', value: 'VR' },
    { label: 'Vibo Valentia', value: 'VV' },
    { label: 'Vicenza', value: 'VI' },
    { label: 'Viterbo', value: 'VT' },
  ],

}
export default lookupsService
